/**
 * Phase F8b BE-1 FE — Admin grant-reattempt CTA + dialog.
 *
 * Surfaces a single "Grant re-attempt" trigger inside the proctor
 * review panel (next to the Recompute CTA). Clicking opens a focused
 * modal asking the IA admin to:
 *
 *   1. Provide a justification (1–500 chars, mandatory) that lands in
 *      the cross-domain audit row, both attempt audit logs, and the
 *      candidate-facing notification payload.
 *   2. Optionally override the template's `generation_mode` for the
 *      fresh attempt — useful when the original draw came from the
 *      pre-gen pool and the admin wants the re-issue to come from
 *      `on_demand` (or vice versa).
 *
 * On success we toast the admin, invalidate the proctor + enquiry
 * caches via the hook, and close the dialog. The IA detail panel
 * re-renders with the fresh tab list — the new attempt becomes the
 * default selection because the panel keys on `assigned_at`.
 *
 * Refusal handling:
 *   * The CTA is disabled when the attempt is in a terminal status
 *     (`submitted` / `graded` / `expired` / `withdrawn`) — a tooltip
 *     explains why and points the admin at the recompute-outcome
 *     path. Defense-in-depth: the BE will still 409 if the FE state
 *     was stale and the admin clicked through.
 *   * 409s from the BE surface verbatim above the form so the admin
 *     can correct course (e.g. another counsellor just submitted).
 *   * 422s render the BE message inline — usually a reason length
 *     boundary the client validation missed (extra whitespace, etc.).
 *
 * Privacy / observability:
 *   * The reason is NOT logged client-side beyond the BE round-trip.
 *     The audit trail lives entirely server-side.
 *   * The dialog clears its `reason` state on close so the next open
 *     starts blank — avoids accidentally re-submitting an old reason
 *     for a different attempt.
 */

import * as React from "react";
import { Loader2, RefreshCw, RotateCcw } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@eduviaos/ui-kit";
import { ApiError, type AssessmentGenerationMode } from "@eduviaos/shared";
import { useGrantReattempt } from "@/hooks/useAdmissions";

const REASON_MIN = 1;
const REASON_MAX = 500;

const TERMINAL_STATUSES = new Set(["submitted", "graded", "expired", "withdrawn"]);

function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status.trim().toLowerCase());
}

interface GrantReattemptDialogProps {
  attemptId: string;
  enquiryId: string;
  attemptStatus: string;
  templateSlug: string;
}

// Privacy note (Iron Rule §13 / Phase F8b): the toast surfaces only
// the templateSlug (which is an admin-shared identifier — never PII)
// and the verb. Reason / candidate identity stay server-side in the
// audit row.
export function GrantReattemptButton(props: GrantReattemptDialogProps) {
  const [open, setOpen] = React.useState(false);
  const terminal = isTerminalStatus(props.attemptStatus);

  const trigger = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={terminal}
      onClick={() => setOpen(true)}
      aria-disabled={terminal}
      data-testid="grant-reattempt-trigger"
    >
      <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
      Grant re-attempt
    </Button>
  );

  return (
    <>
      {terminal ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper because a disabled <button> swallows
                  pointer events and the tooltip would never trigger */}
              <span tabIndex={0}>{trigger}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              This attempt is <strong className="font-semibold">{props.attemptStatus}</strong> — a
              fresh re-attempt can only be granted while the original is still <em>assigned</em> or{" "}
              <em>in&nbsp;progress</em>. To re-resolve a submitted attempt's outcome, use the
              recompute-outcome flow instead.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <GrantReattemptDialog
        open={open}
        onOpenChange={setOpen}
        attemptId={props.attemptId}
        enquiryId={props.enquiryId}
        templateSlug={props.templateSlug}
      />
    </>
  );
}

interface GrantReattemptDialogControlledProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  attemptId: string;
  enquiryId: string;
  templateSlug: string;
}

function GrantReattemptDialog({
  open,
  onOpenChange,
  attemptId,
  enquiryId,
  templateSlug,
}: GrantReattemptDialogControlledProps) {
  const [reason, setReason] = React.useState("");
  const [modeOverride, setModeOverride] = React.useState<AssessmentGenerationMode | "default">(
    "default",
  );
  const [error, setError] = React.useState<string | null>(null);
  const grant = useGrantReattempt(attemptId, enquiryId);

  // Reset transient state every time the dialog flips open. Keeping
  // the previous reason around would be a foot-gun: the next admin
  // (or the same one on a different attempt) might submit it without
  // re-reading.
  React.useEffect(() => {
    if (open) {
      setReason("");
      setModeOverride("default");
      setError(null);
    }
  }, [open]);

  const trimmedLength = reason.trim().length;
  const tooShort = trimmedLength < REASON_MIN;
  const tooLong = trimmedLength > REASON_MAX;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (tooShort) {
      setError("Please give a reason (at least 1 character).");
      return;
    }
    if (tooLong) {
      setError(`Reason must be ${REASON_MAX} characters or fewer.`);
      return;
    }
    try {
      await grant.mutateAsync({
        reason: reason.trim(),
        mode_override: modeOverride === "default" ? null : modeOverride,
      });
      onOpenChange(false);
      toast.success(`Granted a fresh re-attempt for "${templateSlug}".`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Couldn't grant a re-attempt. Please try again.");
        return;
      }
      const msg =
        err instanceof Error ? err.message : "Couldn't grant a re-attempt. Please try again.";
      setError(msg);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!grant.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <RotateCcw className="h-4 w-4 text-amber-600" aria-hidden="true" />
            Grant re-attempt
          </DialogTitle>
          <DialogDescription>
            Invalidates this <span className="font-mono text-xs">{templateSlug}</span> attempt and
            assigns a fresh one to the same candidate. The reason below is audit-logged and surfaced
            in the candidate-facing notification.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-3" noValidate>
          <div className="grid gap-2">
            <Label htmlFor="grant-reattempt-reason" className="text-xs">
              Reason for re-attempt ({REASON_MIN}–{REASON_MAX} characters)
            </Label>
            <Textarea
              id="grant-reattempt-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Browser crashed mid-attempt — re-issuing on the candidate's request."
              rows={3}
              maxLength={REASON_MAX}
              required
              autoFocus
              aria-describedby="grant-reattempt-reason-help"
              data-testid="grant-reattempt-reason"
            />
            <p id="grant-reattempt-reason-help" className="text-muted-foreground text-[11px]">
              {trimmedLength}/{REASON_MAX} characters. The candidate will see this verbatim in their
              notification.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="grant-reattempt-mode" className="text-xs">
              Generation mode for the new attempt
            </Label>
            <Select
              value={modeOverride}
              onValueChange={(v) => setModeOverride(v as AssessmentGenerationMode | "default")}
            >
              <SelectTrigger
                id="grant-reattempt-mode"
                data-testid="grant-reattempt-mode"
                className="h-9 text-xs"
              >
                <SelectValue placeholder="Use template default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Template default</SelectItem>
                <SelectItem value="pre_gen">Pre-generated pool</SelectItem>
                <SelectItem value="on_demand">On-demand generation</SelectItem>
                <SelectItem value="hybrid">Hybrid (pool then on-demand)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-[11px]">
              Optional. Useful when the original attempt drew from a depleted pool and you want the
              re-issue to come fresh from on-demand generation.
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="text-destructive text-xs"
              data-testid="grant-reattempt-error"
            >
              {error}
            </p>
          )}

          <DialogFooter className="mt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={grant.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={grant.isPending || tooShort || tooLong}
              data-testid="grant-reattempt-submit"
            >
              {grant.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Grant re-attempt
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
